import type { ComboItem } from '@kardume/types';

/**
 * Endereço: o que as telas de cadastro de empresa têm em comum.
 *
 * Existe porque o cadastro rápido do guia passou a pedir o mesmo endereço que o
 * cadastro do lojista (rua, número, complemento, bairro, cidade, UF, com busca
 * por CEP). Duas cópias da mesma consulta ao ViaCEP significariam duas telas
 * discordando no dia em que o provedor mudar - e o operador descobrindo isso
 * pelo endereço errado na página pública.
 *
 * Saiu de `apps/backoffice/src/utils` e veio para cá no card #296, quando o
 * painel do lojista ganhou o próprio formulário de endereço: eram três telas em
 * dois apps, e a terceira não tinha como importar de dentro do outro app.
 */

/** Unidades federativas, no formato que o `SearchableComboBox` consome. */
export const UF_OPTIONS: ComboItem[] = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
].map((uf) => ({ id: uf, label: uf }));

/** Só os dígitos, no máximo oito - o que o ViaCEP aceita e o que o core grava. */
export const onlyCepDigits = (raw: string): string => raw.replace(/\D/g, '').slice(0, 8);

/** `12345678` -> `12345-678`. Formata parcial enquanto se digita. */
export const maskCep = (raw: string): string => {
  const d = onlyCepDigits(raw);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

/** O que a consulta devolve, já traduzido para os campos do formulário. */
export interface CepLookupResult {
  street: string;
  district: string;
  city: string;
  state: string;
}

/**
 * Consulta o CEP no ViaCEP.
 *
 * Devolve `null` em CEP incompleto, CEP inexistente ou falha de rede - os três
 * dão no mesmo para quem está preenchendo: o campo continua editável à mão. Não
 * mostra notificação de erro de propósito; digitar `1234` a caminho de um CEP
 * válido não é engano do operador.
 */
export const lookupCep = async (raw: string): Promise<CepLookupResult | null> => {
  const cep = onlyCepDigits(raw);
  if (cep.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await res.json();
    if (data?.erro) return null;
    return {
      street: data.logradouro ?? '',
      district: data.bairro ?? '',
      city: data.localidade ?? '',
      state: data.uf ?? '',
    };
  } catch {
    return null;
  }
};
